import { flatMap, uniq } from 'lodash';
import { translate } from '../../../../../common/intl';
import { Group } from '../../../../../state/ducks/auth/types';
import { FBRequiredApprover } from '../../../../form.builder';
import { ApprovalConfigurationStage, ApprovalConfigurationStageApprover, ApprovalStage, APPROVER_TYPE, StageApprover } from '../../types';
import { ApprovalConfiguration, EditableApprovalConfiguration, OptionType, RoleCoverage } from './types';

export const buildSummary = (approvalStages: ApprovalConfigurationStage[]): string => {
  if (!approvalStages.length) {
    return translate('administration.general.settings.approval.configuration.no.approval.stages');
  }

  const approvalStagesCount = approvalStages.length;
  const allAssignedGroups = uniq(flatMap(approvalStages, 'assignedApprovers')
    ?.filter((approver: ApprovalConfigurationStageApprover) => approver.type === APPROVER_TYPE.GROUP)
    ?.map((approver: ApprovalConfigurationStageApprover) => approver.id));
  const allAssignedUsers = uniq(flatMap(approvalStages, 'assignedApprovers')
    ?.filter((approver: ApprovalConfigurationStageApprover) => approver.type === APPROVER_TYPE.USER)
    ?.map((approver: ApprovalConfigurationStageApprover) => approver.id));

  const assignedGroupsLength = allAssignedGroups.length;
  const assignedUsersLength = allAssignedUsers.length;

  return [
    `${approvalStagesCount} approval ${approvalStagesCount === 1 ? 'stage' : 'stages'}`,
    assignedGroupsLength > 0
      ? `${assignedGroupsLength} ${assignedGroupsLength === 1 ? 'group' : 'groups'}`
      : null,
    assignedUsersLength > 0
      ? `${assignedUsersLength} ${assignedUsersLength === 1 ? 'user' : 'users'}`
      : null,
  ].filter(Boolean).join(' • ');
};

export const enrichConfiguration = (
  configurations: ApprovalConfiguration[], requiredGroups?: Record<string, FBRequiredApprover>, allGroups?: Group[],
): ApprovalConfiguration[] => {
  return configurations.map(configuration => {
    const { approvalStages = [], roleCoverage } = configuration;

    configuration.roleCoverage.coveredRoles
      = buildCoveredRoles(approvalStages, roleCoverage, requiredGroups, allGroups);
    return configuration;
  });
};

export const buildCoveredRoles = (
  approvalStages: ApprovalConfigurationStage[], rolesCoverage: RoleCoverage,
  requiredGroups?: Record<string, FBRequiredApprover>, allGroups?: Group[],
): OptionType[] => {
  const allAssignedApproverIds
    = uniq(flatMap(approvalStages, 'assignedApprovers')
      .filter((approver: ApprovalConfigurationStageApprover) => !approver.isOptional)
      .map((approver: ApprovalConfigurationStageApprover) => approver.id));

  if (requiredGroups && Object.keys(requiredGroups).length) {
    return evaluateRequirementsConfiguration(approvalStages, requiredGroups, allGroups);
  }

  const coveredRoles = rolesCoverage.requiredRoles?.filter(requiredRole => {
    return requiredRole.joinedEmployeeIds.some(joinedEmployee =>
      allAssignedApproverIds.includes(joinedEmployee),
    );
  }).map(group => ({ id: group.id, text: group.text }));
  return coveredRoles ?? [];
};

export const evaluateRequirementsConfiguration = (
  stages: ApprovalConfigurationStage[], requiredGroups?: Record<string, FBRequiredApprover>, allGroups?: Group[],
): OptionType[] => {
  if (!requiredGroups || !Object.values(requiredGroups).length) {
    return [];
  }

  const allRequiredApprovals: ApprovalConfigurationStageApprover[]
    = flatMap(stages, 'assignedApprovers')
      .filter((approver: ApprovalConfigurationStageApprover) => !approver.isOptional);

  const groupApproverIds = uniq(allRequiredApprovals
    .filter(approver => approver.type === APPROVER_TYPE.GROUP)
    .map((approver: ApprovalConfigurationStageApprover) => approver.id));

  const groupEmployeeIds: string[] = uniq(flatMap(allGroups?.filter(group => groupApproverIds.includes(group.id))
    .map(group => group.joinedEmployees.map(employee => employee.id))));

  const userApproverIds = uniq(allRequiredApprovals
    .filter(approver => approver.type === APPROVER_TYPE.USER)
    .map((approver: ApprovalConfigurationStageApprover) => approver.id));

  const allEmployeeIds = [...groupEmployeeIds, ...userApproverIds];

  const checkedGroups = Object.values(requiredGroups)
    .filter(group =>
      group.joinedEmployees.some(employeeId =>
        allEmployeeIds.includes(employeeId),
      ),
    ).map(group => ({ id: group.id, text: group.name }));
  return checkedGroups;
};

export const evaluateRequirements = (stages: ApprovalStage[], requiredGroups?: Record<string, FBRequiredApprover>): OptionType[] => {
  if (!requiredGroups || !Object.values(requiredGroups).length) {
    return [];
  }

  const allAssignedApproverIds = uniq(flatMap(stages, 'assignedApprovers')
    .filter((approver: StageApprover) => !approver.isOptional)
    .map((approver: StageApprover) => approver.approverId));

  const checkedGroups = Object.values(requiredGroups)
    .filter(group =>
      group.joinedEmployees.some(employeeId =>
        allAssignedApproverIds.includes(employeeId),
      ),
    ).map(group => ({ id: group.id, text: group.name }));
  return checkedGroups;
};

export const isConfigurationStagesMapped = (dataItem: EditableApprovalConfiguration): boolean => {
  return dataItem.approvalStages.some(
    stage => stage.editableFields?.length,
  );
};
